import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { debounce } from 'lodash-es';
import QuickSearchResult from './QuickSearchResult';
import MagnifyingGlass from '@Icons/magnifying-glass.svg';
import Close from '../../images/bevego/close.svg';
import { translate } from '../Services/translation';
import {
    query as queryAction,
    setSearchQuery,
    toggleShowFullForm,
    handleKeyDown,
    handleClickSearch,
    toggleShowResult,
} from '../ActionsBevego/QuickSearch.action';
import usePrevious from '@react-hook/previous';

// debouncing function to 200ms so we don't send query request on every key stroke
const debouncedQuery = debounce((dispatch, text) => {
    if (text && text.trim() !== '') {
        // Only dispatch query action if the text is not empty or just whitespace
        dispatch(queryAction(text))
    } else {
        // If the text is empty, dispatch an action to clear results
        dispatch(queryAction(' '));
    }       
}, 200);

const QuickSearch = () => {
    const {
        query,
        result,
        showResult,
        showFullForm,
        selectedItem,
    } = useSelector((state) => state.quickSearch);
    const [lastClickedNode, setLastClickedNode] = useState(null);
    const searchUrl =
        window.__litium.quickSearchUrl +
        (query.length > 0 ? `?q=${query}` : '');
    const searchContainer = useRef(null);
    const searchInput = useRef(null);
    const dispatch = useDispatch();
    const previousSelectedItem = usePrevious(selectedItem);

    const clickHandler = useCallback((event) => {
        setLastClickedNode(event.target);
        const quickSearch = document.getElementById('quickSearch');
        if (quickSearch && !quickSearch.contains(event.target) && showResult) {
            dispatch(toggleShowResult());
            dispatch(setSearchQuery(''));
            searchInput.current.value = '';
        }
    }, [dispatch, showResult]);

    const keyDownHandler = useCallback((event) => {
        if (event.key === 'Escape' && showResult) {
            dispatch(toggleShowResult());
            dispatch(setSearchQuery(''));
            searchInput.current.value = '';
        }
    }, [dispatch, showResult]);

    useEffect(() => {
        // listen for click event to hide the search when clicking outside
        window.addEventListener('mousedown', clickHandler);
        return () => window.removeEventListener('mousedown', clickHandler);
    }, [clickHandler]);

    useEffect(() => {
        // listen for Escape key to close search results
        window.addEventListener('keydown', keyDownHandler);
        return () => window.removeEventListener('keydown', keyDownHandler);
    }, [keyDownHandler]);

    useEffect(() => {
        // set query value if it is available in the Url
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('q')) {
            const query = urlParams.get('q');
            dispatch(setSearchQuery(query));
        }
    }, [dispatch]);

    useEffect(() => {
        if (selectedItem !== previousSelectedItem) {
            const el = document.querySelector(
                '.quick-search-result__item--selected'
            );
            el &&
                el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                    inline: 'nearest',
                });
        }
    }, [selectedItem, previousSelectedItem]);

    const closeResults = () => {
        searchInput.current.value = '';
        dispatch(toggleShowResult());
        dispatch(toggleShowFullForm());
        dispatch(setSearchQuery(''));
    };

    // Handle input focus to load recent searches for empty query
    const handleInputFocus = () => {
        if (!query || query.trim() === '') {
            // The dropdown will only show if there are actual results (handled by the receive action)
            // Send a space character instead of empty string to avoid the validation error
            dispatch(queryAction(' '));
        }
    };

    return (
        <div className="quick-search" role="search" ref={searchContainer} id="quickSearch">
            <div
                className={`quick-search__form ${showFullForm ? 'quick-search__form--force-show' : ''
                    }`}
                role="search"
            >
                <i
                    className="quick-search__icon"
                    onClick={(e) => dispatch(toggleShowFullForm())}
                ></i>
                <div className="quick-search__container">
                    <input
                        className="quick-search__input"
                        type="search"
                        placeholder={translate('general.search')}
                        autoComplete="off"
                        value={decodeURIComponent(query)}
                        onChange={(event) => {
                            const text = encodeURIComponent(event.target.value);
                            dispatch(setSearchQuery(text));
                            debouncedQuery(dispatch, text);
                        }}
                        onFocus={handleInputFocus}
                        onKeyDown={(event) =>
                            dispatch(handleKeyDown(event, { searchUrl }))
                        }
                        ref={searchInput}
                        onBlur={() => {
                            if (
                                searchContainer.current &&
                                !searchContainer.current.contains(
                                    lastClickedNode
                                )
                            ) {
                                showFullForm && dispatch(toggleShowFullForm());
                            }
                        }}
                    />
                    {showResult && (
                        <div className="quick-search__close-result">
                            <a onClick={() => closeResults()}>
                                <Close className="quick-search__close-result--svg" />
                            </a>
                        </div>
                    )}
                    <div className='quick-search__submit-button__wrapper'>
                        <button
                            className="button quick-search__submit-button"
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                dispatch(handleClickSearch({ searchUrl }));
                            }}
                        >
                            <MagnifyingGlass />
                        </button>
                    </div>
                </div>

                {showResult && (
                    <>
                        <QuickSearchResult
                            result={result}
                            selectedItem={selectedItem}
                            searchUrl={searchUrl}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default QuickSearch;